/**
 * Applies a DOM patch to prevent errors when using Google Translate with Next.js
 *
 * This function monkey-patches the DOM Node.prototype methods (removeChild and insertBefore)
 * to handle cases where Google Translate attempts to manipulate DOM nodes with incorrect parent-child
 * relationships. Instead of throwing errors, the patched methods will log warnings and gracefully continue.
 *
 * @remarks
 * This is a workaround for the known issue between Google Translate and React/Next.js DOM reconciliation
 * that causes: "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
 *
 * @see {@link https://github.com/vercel/next.js/issues/58055#issuecomment-3133346877 Next.js issue #58055}
 * @author kasimsen-setur
 * @since 2025-07-31
 *
 * @returns {void}
 */
export default function applyGoogleTranslateDOMPatch() {
  if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;

    Node.prototype.removeChild = function (child) {
      if (child.parentNode !== this) {
        console.warn(
          'Google Translate attempted to remove a child node from the wrong parent. Skipping.',
          {
            childNode: child?.nodeName,
            expectedParent: this?.nodeName,
            actualParent: child?.parentNode?.nodeName,
          }
        );
        return child;
      }

      // eslint-disable-next-line prefer-rest-params
      return originalRemoveChild.apply(this, arguments);
    };

    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.insertBefore = function (newNode, referenceNode) {
      if (referenceNode && referenceNode.parentNode !== this) {
        console.warn(
          'Google Translate attempted to insert before a reference node from the wrong parent. Skipping.',
          {
            newNode: newNode?.nodeName,
            expectedParent: this?.nodeName,
            actualParent: referenceNode?.parentNode?.nodeName,
          }
        );
        return newNode;
      }

      // eslint-disable-next-line prefer-rest-params
      return originalInsertBefore.apply(this, arguments);
    };
  }
}
